import { Link } from "@corpuscomplete/web/components/link";
import { Page, PageContent, PageHeader, PageBody } from "@corpuscomplete/web/components/layout";
import { DateRange, TemporalHelpers } from "@corpuscomplete/core/common/date-time";
import { Button } from "@corpuscomplete/web-ui/components/button";
import {
  Empty,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  EmptyDescription,
} from "@corpuscomplete/web-ui/components/empty";
import { Text } from "@corpuscomplete/web-ui/components/text";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardAction,
} from "@corpuscomplete/web-ui/components/card";
import { Badge } from "@corpuscomplete/web-ui/components/badge";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@corpuscomplete/web-ui/components/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@corpuscomplete/web-ui/components/dialog";
import { useAuth } from "@corpuscomplete/web/auth/hooks";
import {
  PlusIcon,
  MoreHorizontalIcon,
  CalendarIcon,
  UtensilsCrossedIcon,
  PencilIcon,
  CheckCircle2Icon,
  Trash2Icon,
} from "lucide-react";
import { MealPlanList } from "@corpuscomplete/core-ui/nutrition/meal-plan/list";
import { CreateMealPlanDialog } from "../components/create-meal-plan-dialog";
import { useState } from "react";

export function MealPlansPage() {
  const { userId } = useAuth();

  return (
    <MealPlanList.DataProvider userId={userId}>
      <MealPlansPageContent />
    </MealPlanList.DataProvider>
  );
}

export function MealPlansPageContent() {
  const mealPlans = MealPlanList.useMealPlans();
  const { isLoading } = MealPlanList.useLoadingState();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  if (isLoading) {
    return null;
  }

  const sortedPlans = [...mealPlans].sort((a, b) =>
    Temporal.Instant.compare(b.timeCreated, a.timeCreated),
  );

  return (
    <Page>
      <PageBody>
        <PageHeader>
          <Text className="mr-auto">Meal Plans</Text>
          <Button variant="outline" size="sm" onClick={() => setCreateDialogOpen(true)}>
            <PlusIcon className="mr-1 size-3.5" />
            New Plan
          </Button>
        </PageHeader>
        <PageContent>
          {sortedPlans.length === 0 ? (
            <EmptyState onCreateClick={() => setCreateDialogOpen(true)} />
          ) : (
            <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2 lg:grid-cols-3">
              {sortedPlans.map((mealPlan) => (
                <MealPlanCard key={mealPlan.id} mealPlan={mealPlan} />
              ))}
            </div>
          )}
        </PageContent>
        <CreateMealPlanDialog open={createDialogOpen} onOpenChange={setCreateDialogOpen} />
      </PageBody>
    </Page>
  );
}

function EmptyState({ onCreateClick }: { onCreateClick: () => void }) {
  return (
    <Empty className="flex-1">
      <EmptyHeader>
        <EmptyMedia>
          <div className="flex size-16 items-center justify-center rounded-full bg-muted">
            <UtensilsCrossedIcon className="size-8 text-muted-foreground" />
          </div>
        </EmptyMedia>
        <EmptyTitle>No meal plans yet</EmptyTitle>
        <EmptyDescription>
          Create your first meal plan to start organizing your weekly meals and tracking nutrition.
        </EmptyDescription>
      </EmptyHeader>
      <Button onClick={onCreateClick}>
        <PlusIcon className="mr-1.5 size-4" />
        Create Your First Plan
      </Button>
    </Empty>
  );
}

function MealPlanCard({ mealPlan }: { mealPlan: MealPlanList.Preview }) {
  const { deleteMealPlan } = MealPlanList.useMutations();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const createdAt = mealPlan.timeCreated.toZonedDateTimeISO(Temporal.Now.timeZoneId());
  const currentYear = Temporal.Now.plainDateISO().year;

  const statusConfig = {
    draft: {
      label: "Draft",
      variant: "outline" as const,
      icon: PencilIcon,
    },
    published: {
      label: "Published",
      variant: "default" as const,
      icon: CheckCircle2Icon,
    },
  };

  const status = statusConfig[mealPlan.status] ?? statusConfig.draft;
  const StatusIcon = status.icon;

  return (
    <>
      <Card className="group transition-shadow duration-150 hover:ring-2 hover:ring-primary/20">
        <CardHeader>
          <Link
            to="/personal/meal-plans/$id"
            params={{ id: mealPlan.id }}
            className="block min-w-0"
          >
            <CardTitle className="flex items-center gap-2">
              <CalendarIcon className="size-4 text-muted-foreground" />
              <span>{DateRange.format(mealPlan)}</span>
            </CardTitle>
            <CardDescription>{DateRange.formatDuration(mealPlan)}</CardDescription>
          </Link>
          {mealPlan.status === "draft" && (
            <CardAction>
              <DropdownMenu>
                <DropdownMenuTrigger
                  render={
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      className="opacity-0 transition-opacity group-hover:opacity-100 data-[state=open]:opacity-100"
                    />
                  }
                >
                  <MoreHorizontalIcon className="size-4" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    variant="destructive"
                    onClick={() => {
                      setDeleteError(null);
                      setDeleteOpen(true);
                    }}
                  >
                    <Trash2Icon className="mr-2 size-3.5" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </CardAction>
          )}
        </CardHeader>
        <Link to="/personal/meal-plans/$id" params={{ id: mealPlan.id }} className="block">
          <CardContent>
            <div className="flex items-center justify-between">
              <Badge variant={status.variant} className="gap-1">
                <StatusIcon className="size-2.5" />
                {status.label}
              </Badge>
              <Text className="text-muted-foreground">
                {createdAt.year !== currentYear
                  ? TemporalHelpers.formatDate(createdAt.toPlainDate(), "monthDayYear")
                  : TemporalHelpers.formatDate(createdAt.toPlainDate(), "monthDay")}
              </Text>
            </div>
          </CardContent>
        </Link>
      </Card>
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Meal Plan</DialogTitle>
            <DialogDescription>
              Delete this draft meal plan? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {deleteError && <Text className="text-sm text-destructive">{deleteError}</Text>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                const { client } = deleteMealPlan(mealPlan.id);
                void client.then((result) => {
                  if (result.type === "error") {
                    setDeleteError(result.error.message);
                    return;
                  }
                  setDeleteOpen(false);
                });
              }}
            >
              Delete Meal Plan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
